import {FC} from 'react';
import {Footer} from '@components/Footer';
import {SocialsMenu} from '@components/SocialsMenu';
import {Accordion} from '@components/Accordion';
import {ContactBox} from '@components/ContactBox';
import {Row} from '@components/Row';
import {Col} from '@components/Col';
import {Stack} from '@components/Stack';
import {VisuallyHidden} from '@components/VisuallyHidden';
import {Logo} from '@components/Logo';
import {Icon} from '@components/Icon';
import {Nav} from '@components/Nav';
import {FormattedMessage} from 'react-intl';
import {getLocalizedRoute, routes} from '@utils/routes';
import Link from 'next/link';
import {LanguageSwitcher} from './LanguageSwitcher';
import {useLocale} from '@utils/locale';
import {LocaleEnum} from '../../types/general';
import {getCookiesUriForLocale, getTermsUriForLocale} from '@constants/Page';

export const PageFooter: FC = () => {
    const locale = useLocale();

    return (
        <Footer>
            <Row className="justify-content-between flex-xxl-nowrap">
                <Col lg="auto" md={8}>
                    <Footer.SideContent>
                        <Stack>
                            <Footer.Logo>
                                <Link href={getLocalizedRoute(routes.homepage, locale)}>
                                    <a>
                                        <VisuallyHidden>Bezrealitky</VisuallyHidden>

                                        <Logo width={165} />
                                    </a>
                                </Link>
                            </Footer.Logo>

                            <Footer.Claim>
                                <p>
                                    <FormattedMessage
                                        defaultMessage={'Zajišťujeme prodej a pronájem nemovitostí bez provize a s kompletním servisem.'}
                                    />
                                </p>
                            </Footer.Claim>

                            <Footer.Socials>
                                <SocialsMenu>
                                    <SocialsMenu.Link variant="facebook">
                                        <a
                                            href={
                                                locale === LocaleEnum.sk
                                                    ? 'https://www.facebook.com/bezrealitky.sk'
                                                    : 'https://www.facebook.com/bezrealitky'
                                            }
                                        >
                                            <Icon.Facebook size="lg" />

                                            <VisuallyHidden>
                                                <FormattedMessage defaultMessage={'Bezrealitky na Facebooku'} />
                                            </VisuallyHidden>
                                        </a>
                                    </SocialsMenu.Link>

                                    <SocialsMenu.Link variant="instagram">
                                        <a
                                            href={
                                                locale === LocaleEnum.sk
                                                    ? 'https://www.instagram.com/bezrealitky.sk/'
                                                    : 'https://www.instagram.com/bezrealitky.cz/'
                                            }
                                        >
                                            <Icon.Instagram size="lg" />

                                            <VisuallyHidden>
                                                <FormattedMessage defaultMessage={'Bezrealitky na Instagramu'} />
                                            </VisuallyHidden>
                                        </a>
                                    </SocialsMenu.Link>
                                </SocialsMenu>
                            </Footer.Socials>
                        </Stack>
                    </Footer.SideContent>
                </Col>

                <Col
                    md={{
                        order: 'last',
                    }}
                    xxl={{
                        order: 0,
                        span: 'auto',
                    }}
                >
                    <Footer.Nav>
                        <Row>
                            <Col md xs="12">
                                <Accordion>
                                    <Footer.NavTitle>
                                        <Accordion.Header as="span">
                                            <FormattedMessage defaultMessage={'Zájemci'} />
                                        </Accordion.Header>
                                    </Footer.NavTitle>

                                    <Accordion.Body>
                                        <Nav as="ul" className="flex-column">
                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.list, locale, {list: ''})} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Nemovitosti'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentreIntroCustomer, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Tipy pro zájemce'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentrePremium, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Premium profil'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Nav.Link href="https://hypoteka.bezrealitky.cz/">
                                                    <FormattedMessage defaultMessage={'Hypotéka'} />
                                                </Nav.Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentreEnergy, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Převod energií'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentreWatchdog, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Hlídací pes'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>
                                        </Nav>
                                    </Accordion.Body>
                                </Accordion>
                            </Col>

                            <Col md xs="12">
                                <Accordion>
                                    <Footer.NavTitle>
                                        <Accordion.Header as="span">
                                            <FormattedMessage defaultMessage={'Majitelé'} />
                                        </Accordion.Header>
                                    </Footer.NavTitle>

                                    <Accordion.Body>
                                        <Nav as="ul" className="flex-column">
                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentreIntroOwner, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage="Nabídnout nemovitost" />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.serviceCentreSale, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Služby'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>

                                            <Nav.Item as="li">
                                                <Link href={getLocalizedRoute(routes.priceList, locale)} passHref>
                                                    <Nav.Link>
                                                        <FormattedMessage defaultMessage={'Ceník inzerce'} />
                                                    </Nav.Link>
                                                </Link>
                                            </Nav.Item>
                                        </Nav>
                                    </Accordion.Body>
                                </Accordion>
                            </Col>

                            <Col md xs="12">
                                <Footer.NavTitle>
                                    <FormattedMessage defaultMessage={'Další informace'} />
                                </Footer.NavTitle>

                                <Nav as="ul" className="flex-column">
                                    <Nav.Item as="li">
                                        <Link href={getLocalizedRoute(routes.aboutUs, locale)} passHref>
                                            <Nav.Link>
                                                <FormattedMessage defaultMessage={'O nás'} />
                                            </Nav.Link>
                                        </Link>
                                    </Nav.Item>

                                    <Nav.Item as="li">
                                        <Nav.Link
                                            href={
                                                locale === LocaleEnum.sk
                                                    ? 'https://www.bezrealitky.sk/blog/'
                                                    : 'https://blog.bezrealitky.cz/'
                                            }
                                        >
                                            <FormattedMessage defaultMessage={'Blog Bezrealitky'} />
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                            </Col>
                        </Row>
                    </Footer.Nav>
                </Col>

                <Col lg="auto" md={8}>
                    <Footer.SideContent>
                        <div
                            style={{
                                maxWidth: '26rem',
                            }}
                        >
                            {locale === LocaleEnum.sk ? (
                                <ContactBox>
                                    <ContactBox.Heading>
                                        <FormattedMessage defaultMessage={'Rádi vám poradíme'} />
                                    </ContactBox.Heading>

                                    <ContactBox.Link href="tel:+421220570345" iconName="Phone">
                                        +421 220 570 345
                                        <ContactBox.LinkDesc>
                                            <FormattedMessage defaultMessage={'Po - Pá (8:00 - 17:00)'} />
                                        </ContactBox.LinkDesc>
                                    </ContactBox.Link>

                                    <ContactBox.Link href="mailto:info@bezrealitky.sk" iconName="Envelope">
                                        info@bezrealitky.sk
                                    </ContactBox.Link>
                                </ContactBox>
                            ) : (
                                <ContactBox>
                                    <ContactBox.Heading>
                                        <FormattedMessage defaultMessage={'Rádi vám poradíme'} />
                                    </ContactBox.Heading>

                                    <ContactBox.Link href="tel:+420226227522" iconName="Phone">
                                        +420 226 227 522
                                        <ContactBox.LinkDesc>
                                            <FormattedMessage defaultMessage={'Po - Pá (8:00 - 17:00)'} />
                                        </ContactBox.LinkDesc>
                                    </ContactBox.Link>

                                    <ContactBox.Link href="mailto:info@bezrealitky.cz" iconName="Envelope">
                                        info@bezrealitky.cz
                                    </ContactBox.Link>
                                </ContactBox>
                            )}
                        </div>
                    </Footer.SideContent>
                </Col>
            </Row>

            <Footer.Bottom>
                <Footer.Copyright>
                    <Footer.CopyrightItem variant="year">© 2022 Bezrealitky</Footer.CopyrightItem>

                    <Footer.CopyrightItem variant="copy">
                        <FormattedMessage defaultMessage={'Všechna práva vyhrazena'} />
                    </Footer.CopyrightItem>

                    <Footer.CopyrightItem variant="link">
                        <Link
                            href={getLocalizedRoute(routes.page, locale, {
                                uri: getCookiesUriForLocale(locale),
                            })}
                            passHref
                        >
                            <a>
                                <FormattedMessage defaultMessage={'Soukromí'} />
                            </a>
                        </Link>
                    </Footer.CopyrightItem>

                    <Footer.CopyrightItem variant="link">
                        <Link
                            href={getLocalizedRoute(routes.page, locale, {
                                uri: getTermsUriForLocale(locale),
                            })}
                            passHref
                        >
                            <a>
                                <FormattedMessage defaultMessage={'Podmínky'} />
                            </a>
                        </Link>
                    </Footer.CopyrightItem>

                    <Footer.CopyrightItem>
                        <a href="https://www.id-sign.com" target="_blank">
                            <FormattedMessage defaultMessage="Vytvářeno v iD-SIGN" />
                        </a>
                    </Footer.CopyrightItem>

                    <Footer.CopyrightItem variant="lang-switcher">
                        <LanguageSwitcher />
                    </Footer.CopyrightItem>
                </Footer.Copyright>
            </Footer.Bottom>
        </Footer>
    );
};
